import { nanoid } from 'nanoid';
import axios, { AxiosResponse } from 'axios';
import api from 'app/api';
import { receiveError, toastUpdate } from './showSuccessErrorModal';
import { toast } from 'react-toastify';

export const handleUpload = async (setIsFilesUploading, files, setFiles) => {
  setIsFilesUploading(1);

  const promises = files.map(async file => {
    const info = await fileUpload(file);
    return info;
  });

  const newDocuments = await Promise.all(promises);

  setFiles([]);
  setIsFilesUploading(2);

  return newDocuments;
};

const fileUpload = async (file: File) => {
  try {
    const newFileName = encodeURI(nanoid() + file.name);
    const newFile = new File([file], newFileName, { type: file.type });
    const response: AxiosResponse = await api.get('messages/presigned-url', {
      params: {
        file_name: newFile.name,
        file_type: newFile.type,
      },
    });
    const { signedRequest, url } = response.data.data;

    // Once we have received the pre-signed URL, we can upload the file to S3
    const result = await uploadFileToS3(newFile, signedRequest);
    const info = {
      name: file.name,
      size: newFile.size,
      type: newFile.type,
      url,
    };
    if (result.success) return info;
    else return '';
  } catch (error) {
    receiveError(error, 'Failed to get the pre-signed URL ');
    return '';
  }

  // If successful, the file is now uploaded. You can get the public URL for this file from 'url' variable
};

export interface UploadProgressEvent {
  loaded: number;
  total: number;
  percent: number;
}

export interface UploadResult {
  success: boolean;
  status?: number;
  statusText?: string;
  error?: string;
}

/**
 * Generic PUT to a presigned S3 URL. Caller supplies its own progress UI
 * via onProgress; this helper does no toasts/notifications. Use this from
 * any feature that has a presigned URL (media uploads, copilot scratch,
 * future integrations).
 */
export const uploadFileToSignedUrl = async (
  file: File | Blob,
  signedUrl: string,
  options?: {
    contentType?: string;
    onProgress?: (ev: UploadProgressEvent) => void;
  },
): Promise<UploadResult> => {
  if (!signedUrl) return { success: false, error: 'Signed URL is undefined' };
  try {
    const response = await axios.put(signedUrl, file, {
      headers: {
        'Content-Type':
          options?.contentType ??
          (file instanceof File
            ? file.type || 'application/octet-stream'
            : 'application/octet-stream'),
      },
      onUploadProgress: progressEvent => {
        const total = progressEvent.total;
        const loaded = progressEvent.loaded;
        if (typeof total === 'number' && options?.onProgress) {
          options.onProgress({
            loaded,
            total,
            percent: Math.floor((loaded / total) * 100),
          });
        }
      },
    });
    return {
      success: true,
      status: response.status,
      statusText: response.statusText,
    };
  } catch (error: unknown) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Upload failed',
    };
  }
};

const uploadFileToS3 = async (
  file: File,
  signedUrl: string,
): Promise<UploadResult> => {
  const toastId = toast(`Starting upload this ${file.name}...`, {
    progress: 0,
    autoClose: false,
  });

  const result = await uploadFileToSignedUrl(file, signedUrl, {
    onProgress: ev => toastUpdate(file.name, ev.percent, toastId),
  });

  if (result.success) {
    toast.update(toastId, {
      render: 'Upload complete!',
      type: 'success',
      autoClose: 5000,
    });
  } else {
    receiveError(
      { message: result.error ?? 'File upload failed' },
      'File upload failed: ',
    );
  }
  return result;
};

export default fileUpload;
